import {match} from 'ts-pattern';

import {equals, isNil} from 'ramda';

import type {BulkOperationResponseBody} from '../types/api/bulkOperations';
import type {BulkOperationProgress} from '../types/BulkOperationsServices';
import {getBulkOperationProcessedCount} from './getBulkOperationProcessedCount';
import {isBulkOperationTerminal} from './isBulkOperationTerminal';

export type BulkOperationRecord =
  | {type: 'detail'; value: BulkOperationResponseBody}
  | {type: 'progress'; value: BulkOperationProgress};

/** Progress has no revision number. Preserve accepted progress while hydrating final detail. */
export function reconcileBulkOperation(
  previous: BulkOperationRecord | undefined,
  update: BulkOperationRecord
): BulkOperationRecord {
  if (isNil(previous)) {
    return update;
  }
  const current = previous.value;
  const incoming = update.value;
  const currentTerminal = isBulkOperationTerminal(current.state);
  const incomingTerminal = isBulkOperationTerminal(incoming.state);
  const next = {...current, ...incoming};
  const regresses =
    (currentTerminal && !incomingTerminal) ||
    (current.state === 'IN_PROGRESS' && incoming.state === 'PENDING') ||
    getBulkOperationProcessedCount(next) < getBulkOperationProcessedCount(current);
  const currentDetail = previous.type === 'detail' ? previous.value : undefined;
  const incomingDetail = update.type === 'detail' ? update.value : undefined;
  return match({
    sameOperation: current.bulkOperationId === incoming.bulkOperationId,
    regresses,
    canHydrate:
      !isNil(incomingDetail) && (isNil(currentDetail) || (currentTerminal && incomingTerminal)),
  })
    .with({sameOperation: false}, () => previous)
    .with({regresses: true, canHydrate: false}, () => previous)
    .otherwise(() => {
      const detail = incomingDetail ?? currentDetail;
      const progress = regresses ? current : incoming;
      const operation: BulkOperationRecord = isNil(detail)
        ? {type: 'progress', value: {...current, ...progress}}
        : {
            type: 'detail',
            value: {
              ...detail,
              ...progress,
              errorCode: detail.errorCode ?? currentDetail?.errorCode ?? null,
              errorMessage: detail.errorMessage ?? currentDetail?.errorMessage ?? null,
              finishedAt: detail.finishedAt ?? currentDetail?.finishedAt ?? null,
            },
          };
      return equals(previous, operation) ? previous : operation;
    });
}
